import React, { useState } from 'react';
import { sanitizeText, sanitizeInstagramHandle, createInstagramURL, createMapsURL } from '../utils/sanitize';
import { validateEventTitle, validateEventDescription, validateEventLocation } from '../utils/validation';
import { generateGoogleCalendarUrl, generateOutlookCalendarUrl, generateICSFile } from '../utils/calendar';
import { SERVICE_OPTIONS } from './OnboardingModal';

// Helper to get service display info
const getServiceInfo = (serviceKey) => {
  return SERVICE_OPTIONS.find(s => s.key === serviceKey) || null;
};

function Sidebar({ 
  locations, 
  events, 
  user, 
  filteredTruck,
  serviceFilters,
  onAddEvent, 
  onUpdateEvent,
  onDeleteEvent, 
  onSelectLocation,
  onViewTruckOnly,
  onGetShareableUrl,
  onToggleStatus,
  onServiceFilterChange,
  onOpenOnboarding
}) {
  const [activeTab, setActiveTab] = useState('events');
  const [editingEvent, setEditingEvent] = useState(null);
  const [calendarDropdownOpen, setCalendarDropdownOpen] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    start_time: '',
    end_time: '',
    auto_delete: false,
    location: '',
  });

  // Find the location for a given event's user
  const getLocationForEvent = (event) => {
    return locations.find(l => l.user_id === event.user_id);
  };

  // Check if an event has expired
  const isEventExpired = (event) => {
    if (!event.end_time) return false;
    return new Date(event.end_time) < new Date();
  };

  const handleEventClick = (event) => {
    const location = getLocationForEvent(event);
    if (location) {
      onSelectLocation(location);
    }
  };

  const handleShareTruck = async (location, event) => {
    if (!location.slug) return;
    
    const shareableUrl = onGetShareableUrl(location.slug);
    
    try {
      // Try to use the Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: `${location.truck_name || location.username} - AVL Flavor`,
          text: `Check out ${location.truck_name || location.username} on AVL Flavor!`,
          url: shareableUrl,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareableUrl);
        // Show a temporary notification
        const button = event.target.closest('.share-btn');
        const originalText = button.innerHTML;
        button.innerHTML = '✅ Copied!';
        button.style.backgroundColor = '#4CAF50';
        setTimeout(() => {
          button.innerHTML = originalText;
          button.style.backgroundColor = '';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to share:', err);
      // Fallback: show the URL in an alert
      alert(`Share this link: ${shareableUrl}`);
    }
  };

  const handleEventSubmit = (e) => {
    e.preventDefault();
    
    // Validate form inputs
    const titleValidation = validateEventTitle(eventForm.title);
    if (!titleValidation.isValid) {
      alert(titleValidation.error);
      return;
    }
    
    const descriptionValidation = validateEventDescription(eventForm.description);
    if (!descriptionValidation.isValid) {
      alert(descriptionValidation.error);
      return;
    }
    
    const locationValidation = validateEventLocation(eventForm.location);
    if (!locationValidation.isValid) {
      alert(locationValidation.error);
      return;
    }
    
    // Create sanitized event data
    const sanitizedEventData = {
      ...eventForm,
      title: titleValidation.value,
      description: descriptionValidation.value,
      location: locationValidation.value
    };
    
    if (editingEvent) {
      // Update existing event
      onUpdateEvent(editingEvent.id, sanitizedEventData);
      setEditingEvent(null);
    } else {
      // Add new event
      onAddEvent(sanitizedEventData);
    }
    
    setEventForm({
      title: '',
      description: '',
      event_date: '',
      start_time: '',
      end_time: '',
      auto_delete: false,
      location: '',
    });
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      event_date: event.event_date ? event.event_date.split('T')[0] : '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      auto_delete: event.auto_delete || false,
      location: event.location || '',
    });
    // Switch to events tab if not already there
    setActiveTab('events');
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      event_date: '',
      start_time: '',
      end_time: '',
      auto_delete: false,
      location: '',
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-tabs">
        <button
          className={activeTab === 'events' ? 'active' : ''}
          onClick={() => setActiveTab('events')}
        >
          📢 Events
        </button>
        <button
          className={activeTab === 'trucks' ? 'active' : ''}
          onClick={() => setActiveTab('trucks')}
        >
          🚚 Trucks ({locations.length})
        </button>
      </div>

      {filteredTruck && (
        <div className="filtered-truck-banner">
          <div className="filtered-truck-content">
            <h3>🚚 {sanitizeText(filteredTruck.truck_name || filteredTruck.username)}</h3>
            {filteredTruck.instagram && (
              <a 
                href={createInstagramURL(filteredTruck.instagram)}
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-link"
              >
                📸 @{sanitizeInstagramHandle(filteredTruck.instagram)}
              </a>
            )}
            {filteredTruck.description && (
              <p>{sanitizeText(filteredTruck.description)}</p>
            )}
          </div>
        </div>
      )}

      <div className="sidebar-content">
        {activeTab === 'events' && (
          <div className="event-wall">
            {filteredTruck && events.length === 0 && (
              <div className="no-events-message">
                <p>No events found for {sanitizeText(filteredTruck.truck_name || filteredTruck.username)}.</p>
              </div>
            )}
            
            {user && (!filteredTruck || (filteredTruck && user.slug === filteredTruck.slug)) && (
              <form className={`event-form ${editingEvent ? 'edit-mode' : ''}`} onSubmit={handleEventSubmit}>
                {editingEvent && (
                  <div className="edit-mode-header">
                    <span>✏️ Editing Event</span>
                    <button type="button" className="cancel-edit-btn" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Event title..."
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  required
                />
                <textarea
                  placeholder="Tell us about your event..."
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                />
                <input
                  type="datetime-local"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                />
                <div className="timeframe-inputs">
                  <label>Event Timeframe (optional):</label>
                  <div className="timeframe-row">
                    <div className="timeframe-field">
                      <span>Start:</span>
                      <input
                        type="datetime-local"
                        value={eventForm.start_time}
                        onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                      />
                    </div>
                    <div className="timeframe-field">
                      <span>End:</span>
                      <input
                        type="datetime-local"
                        value={eventForm.end_time}
                        onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                      />
                    </div>
                  </div>
                  {eventForm.end_time && (
                    <label className="auto-delete-checkbox">
                      <input
                        type="checkbox"
                        checked={eventForm.auto_delete}
                        onChange={(e) => setEventForm({ ...eventForm, auto_delete: e.target.checked })}
                      />
                      <span>Auto-delete event and remove pin when timeframe ends</span>
                    </label>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Location / Address"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                />
                <button type="submit">{editingEvent ? 'Update Event' : 'Post Event'}</button>
              </form>
            )}

            <div className="event-list">
              {events.length === 0 ? (
                <p className="loading">
                  {filteredTruck 
                    ? `No events from ${sanitizeText(filteredTruck.truck_name || filteredTruck.username)} yet.` 
                    : 'No events yet. Be the first to post!'}
                </p>
              ) : (
                events.map((event) => {
                  const eventLocation = getLocationForEvent(event);
                  const expired = isEventExpired(event);
                  return (
                    <div 
                      key={event.id} 
                      className={`event-card ${eventLocation ? 'clickable' : ''} ${expired ? 'expired' : ''}`}
                      onClick={() => handleEventClick(event)}
                    >
                      {expired && (
                        <div className="expired-badge">⏰ Event Ended</div>
                      )}
                      <h3>{sanitizeText(event.title)}</h3>
                      <div className="event-meta">
                        <span>🚚 {sanitizeText(event.truck_name || event.username)}</span>
                        <span> • {formatTimeAgo(event.created_at)}</span>
                      </div>
                      {event.description && <p>{sanitizeText(event.description)}</p>}
                      {event.event_date && (
                        <p><strong>📅</strong> {formatDate(event.event_date)}</p>
                      )}
                      {(event.start_time || event.end_time) && (
                        <div className="event-timeframe">
                          <div className="timeframe-label"><strong>⏱️ Timeframe:</strong></div>
                          <div className="timeframe-times">
                            {event.start_time && <span className="timeframe-start">{formatDateTime(event.start_time)}</span>}
                            {event.start_time && event.end_time && <span className="timeframe-arrow"> → </span>}
                            {event.end_time && <span className="timeframe-end">{formatDateTime(event.end_time)}</span>}
                          </div>
                          {event.auto_delete === 1 && (
                            <div className="auto-delete-notice">🔄 Auto-removes when ended</div>
                          )}
                        </div>
                      )}
                      {event.location && (
                        <p><strong>📍</strong> {sanitizeText(event.location)}</p>
                      )}
                      {eventLocation && (
                        <div className="event-actions">
                          <span className="view-on-map">📍 Click to view on map</span>
                          <a
                            href={createMapsURL(eventLocation.latitude, eventLocation.longitude)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="directions-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🗺️ Get Directions
                          </a>
                        </div>
                      )}
                      
                      {/* Calendar Integration */}
                      <div className="calendar-actions">
                        <div className="calendar-dropdown-container">
                          <button
                            className="calendar-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCalendarDropdownOpen(calendarDropdownOpen === event.id ? null : event.id);
                            }}
                          >
                            📅 Add to Calendar
                          </button>
                          {calendarDropdownOpen === event.id && (
                            <div className="calendar-dropdown">
                              <a
                                href={generateGoogleCalendarUrl(event)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                🔴 Google Calendar
                              </a>
                              <a
                                href={generateOutlookCalendarUrl(event)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                🔵 Outlook Calendar
                              </a>
                              <a
                                href={generateICSFile(event)}
                                download={`${event.title || 'event'}.ics`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                📥 Download .ics
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {user && user.id === event.user_id && (
                        <div className="event-owner-actions">
                          <button
                            className="edit-btn"
                            onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={(e) => { e.stopPropagation(); onDeleteEvent(event.id); }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'trucks' && (
          <div className="truck-list">
            {/* Service Filters */}
            <div className="service-filters">
              <div className="filter-header">
                <span>🔍 Filter by services:</span>
                {serviceFilters && serviceFilters.length > 0 && (
                  <button 
                    className="clear-filters-btn"
                    onClick={() => onServiceFilterChange([])}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filter-chips">
                {SERVICE_OPTIONS.map(service => (
                  <button
                    key={service.key}
                    className={`filter-chip ${serviceFilters?.includes(service.key) ? 'active' : ''}`}
                    onClick={() => {
                      const newFilters = serviceFilters?.includes(service.key)
                        ? serviceFilters.filter(f => f !== service.key)
                        : [...(serviceFilters || []), service.key];
                      onServiceFilterChange(newFilters);
                    }}
                  >
                    {service.icon} {service.label}
                  </button>
                ))}
              </div>
            </div>

            {locations.length === 0 ? (
              <p className="loading">
                {serviceFilters && serviceFilters.length > 0 
                  ? 'No trucks match your filter criteria.' 
                  : 'No trucks are currently active.'}
              </p>
            ) : (
              locations.map((location) => {
                const isOpen = location.is_open === 1 || location.is_open === true;
                const isOwner = user && user.id === location.user_id;
                
                // Get active services for this location
                const activeServices = location.services 
                  ? SERVICE_OPTIONS.filter(s => location.services[s.key])
                  : [];
                
                return (
                  <div
                    key={location.id}
                    className={`truck-card ${!isOpen ? 'truck-closed' : ''}`}
                    onClick={() => onSelectLocation(location)}
                  >
                    <div className="truck-header">
                      <h3>{sanitizeText(location.truck_name || location.username)}</h3>
                      <span className={`status-badge ${isOpen ? 'status-open' : 'status-closed'}`}>
                        {isOpen ? '🟢 Open' : '🔴 Closed'}
                      </span>
                    </div>
                    <p className="truck-owner">@{sanitizeText(location.username)}</p>
                    {location.instagram && (
                      <a 
                        href={createInstagramURL(location.instagram)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="instagram-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📸 @{sanitizeInstagramHandle(location.instagram)}
                      </a>
                    )}
                    {location.truck_description && (
                      <p>{sanitizeText(location.truck_description)}</p>
                    )}
                    {location.address && (
                      <p><strong>📍</strong> {sanitizeText(location.address)}</p>
                    )}
                    
                    {/* Service Badges */}
                    {activeServices.length > 0 && (
                      <div className="service-badges">
                        {activeServices.map(service => (
                          <span key={service.key} className="service-badge" title={service.label}>
                            {service.icon}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <p className="truck-owner">
                      Last updated: {formatTimeAgo(location.updated_at)}
                    </p>
                    <div className="truck-actions">
                      {isOwner && (
                        <button
                          className={`status-toggle-btn ${isOpen ? 'toggle-close' : 'toggle-open'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleStatus(!isOpen);
                          }}
                        >
                          {isOpen ? '🔴 Mark as Closed' : '🟢 Mark as Open'}
                        </button>
                      )}
                      {isOwner && (
                        <button
                          className="edit-services-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenOnboarding();
                          }}
                        >
                          ⚙️ Edit Services
                        </button>
                      )}
                      <a
                        href={createMapsURL(location.latitude, location.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="directions-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🗺️ Get Directions
                      </a>
                      {location.slug && (
                        <>
                          <button
                            className="share-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareTruck(location, e);
                            }}
                          >
                            📤 Share Link
                          </button>
                          {!filteredTruck && (
                            <button
                              className="view-only-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewTruckOnly(location.slug);
                              }}
                            >
                              👁️ View Only This Truck
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
